import { Anchor, Button, Center, Group, Loader, Stack, Text, TextInput, Title } from '@mantine/core'
import { hasLength, isEmail, isNotEmpty, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconMail } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useParams } from 'wouter'

import type { RouteParams } from '@/configs/routes'
import { useGlobalState } from '@/hooks/useGlobalState'
import {
  ChannelBrandHeader,
  resolveBrandImageUrl,
  Shell,
} from '@/screens/ChannelRegistrationScreen/channelRegistrationShared'
import { FriendlyCaptcha, type FriendlyCaptchaRef } from '@/screens/OnboardingScreen/FriendlyCaptcha'
import { ERROR_MESSAGES, MIN_NAME_LENGTH } from '@/utils/onboardingConstants'
import { handleOnboardingError, showOnboardingErrorNotification } from '@/utils/onboardingErrorHandling'

type RegistrationFormValues = {
  name: string
  email: string
  confirmEmail: string
}

export function ChannelRegistrationScreen() {
  const { api, configStore } = useGlobalState()
  const { channelId } = useParams<RouteParams['channelRegistration']>()

  const [captchaResponse, setCaptchaResponse] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState('')
  const captchaRef = useRef<FriendlyCaptchaRef>(null)

  const captchaSitekey = configStore.captchaSitekey || 'test-sitekey'

  const channelQuery = useQuery({
    queryKey: ['channel', 'public', channelId],
    queryFn: () => api.getChannelPublicInfo(channelId),
    retry: false,
  })

  // Only /channel_info returns a full-URL brand_image; fetched purely for the logo.
  const channelInfoQuery = useQuery({
    queryKey: ['channel', 'info-public', channelId],
    queryFn: () => api.getChannelInfo(channelId),
    retry: false,
  })

  const form = useForm<RegistrationFormValues>({
    initialValues: { name: '', email: '', confirmEmail: '' },
    validate: {
      name: hasLength({ min: MIN_NAME_LENGTH }, ERROR_MESSAGES.CONTACT_NAME_TOO_SHORT),
      email: isEmail(ERROR_MESSAGES.EMAIL_INVALID),
      confirmEmail: (value, values) => {
        if (!isNotEmpty(value)) return ERROR_MESSAGES.CONFIRM_EMAIL_REQUIRED
        if (value !== values.email) return ERROR_MESSAGES.EMAIL_MISMATCH
        return null
      },
    },
    validateInputOnBlur: true,
  })

  const registerMutation = useMutation({
    mutationFn: (values: RegistrationFormValues) =>
      api.registerEndUser({
        channel_id: channelId,
        name: values.name,
        email: values.email,
        'frc-captcha-response': captchaResponse ?? '',
      }),
    onSuccess: (_data, variables) => {
      setRegisteredEmail(variables.email)
      setIsComplete(true)
    },
    onError: error => {
      const onboardingError = handleOnboardingError(error, 'register')
      showOnboardingErrorNotification(onboardingError, 'registration')
      setCaptchaResponse(null)
      captchaRef.current?.reset()
    },
  })

  const handleSubmit = (values: RegistrationFormValues) => {
    if (!captchaResponse) {
      notifications.show({
        title: 'Captcha Required',
        message: ERROR_MESSAGES.CAPTCHA_REQUIRED,
        color: 'red',
      })
      return
    }
    registerMutation.mutate(values)
  }

  if (channelQuery.isLoading) {
    return (
      <Shell srHeading="Register for a Sidekick channel">
        <Center mih={400}>
          <Loader />
        </Center>
      </Shell>
    )
  }

  if (channelQuery.isError || !channelQuery.data) {
    return (
      <Shell srHeading="Register for a Sidekick channel">
        <Stack gap="md" mt="xl" maw={560} mx="auto" ta="center">
          <Title order={4}>Channel not found</Title>
          <Text c="dimmed">
            We couldn&apos;t find the channel you&apos;re looking for. Please double-check the link or contact the
            channel administrator.
          </Text>
        </Stack>
      </Shell>
    )
  }

  const channel = channelQuery.data

  if (!channel.selfRegistrationEnabled) {
    return (
      <Shell srHeading="Register for a Sidekick channel">
        <Stack gap="md" mt="xl" maw={560} mx="auto" ta="center">
          <Title order={4}>Registration unavailable</Title>
          <Text c="dimmed">
            Self-registration is not currently available for {channel.brandTitle || channel.name}. Please contact the
            channel administrator for access.
          </Text>
        </Stack>
      </Shell>
    )
  }

  const brandImageUrl = resolveBrandImageUrl(
    channelInfoQuery.data?.brandImage ?? channel.brandImage,
    configStore.serverBaseUrl,
  )

  if (isComplete) {
    return (
      <Shell srHeading="Register for a Sidekick channel">
        <Stack gap="lg" mt="xl" maw={560} mx="auto" align="center">
          <ChannelBrandHeader channel={channel} imageUrl={brandImageUrl} />
          <Stack gap="xs" ta="center" align="center" maw={420}>
            <IconMail size={48} stroke={1.5} />
            <Title order={4}>Check your email to finish signing up</Title>
            <Text c="dimmed">
              We sent an email to:
              <br />
              <Text component="span" c="blue" fw={500}>
                {registeredEmail}
              </Text>
            </Text>
            <Text c="dimmed" mt="sm">
              Open the email to find your temporary login details. Use them to log into the app.
            </Text>
            <Text c="dimmed" size="sm" mt="md">
              Can&apos;t find it? Check your spam or promotions folder.
            </Text>
          </Stack>
        </Stack>
      </Shell>
    )
  }

  return (
    <Shell srHeading="Register for a Sidekick channel">
      <Stack gap="md" mt="xl" maw={560} mx="auto">
        <ChannelBrandHeader channel={channel} imageUrl={brandImageUrl} />
        <Text size="sm" c="dimmed" ta="center">
          Register below to join the channel.
        </Text>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md" my="xl">
            <TextInput label="Name" placeholder="Your full name" required {...form.getInputProps('name')} />
            <TextInput
              label="Email"
              placeholder="your.email@company.com"
              type="email"
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Confirm Email"
              placeholder="Confirm your email address"
              type="email"
              required
              {...form.getInputProps('confirmEmail')}
            />

            <Group mt="md">
              <FriendlyCaptcha
                ref={captchaRef}
                sitekey={captchaSitekey}
                onVerify={setCaptchaResponse}
                onError={() => {
                  notifications.show({
                    title: 'Captcha Error',
                    message: ERROR_MESSAGES.CAPTCHA_ERROR,
                    color: 'red',
                  })
                }}
              />
            </Group>

            <Text size="sm" c="dimmed" mt="md">
              By registering, you agree to the{' '}
              <Anchor href="https://sidekickwellness.com/tos.html" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </Anchor>
              . For more information, see the{' '}
              <Anchor href="https://sidekickwellness.com/privacy.html" target="_blank" rel="noopener noreferrer">
                Sidekick Wellness Privacy Statement
              </Anchor>
              .
            </Text>

            <Group justify="flex-end" mt="xl">
              <Button type="submit" loading={registerMutation.isPending} disabled={!form.isValid() || !captchaResponse}>
                Register
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Shell>
  )
}
